/**
 * Zone switcher — pop-up "Choisissez votre zone et votre langue".
 * Markup : sections/zone-switcher.liquid
 * Déclencheurs : tout élément [data-zone-switcher-open] (masqué via [hidden] tant que le composant n'est pas prêt).
 */
if (!customElements.get('zone-switcher')) {
  const STORAGE_KEY = 'fauchon:zone-switcher';

  const storage = {
    get() {
      try {
        return window.localStorage.getItem(STORAGE_KEY);
      } catch (e) {
        return null;
      }
    },
    set(value) {
      try {
        window.localStorage.setItem(STORAGE_KEY, value);
      } catch (e) {
        /* navigation privée / stockage bloqué : la pop-up réapparaîtra, sans gravité */
      }
    },
  };

  class ZoneSwitcher extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-zone-switcher-form]');
      this.zoneSelect = this.querySelector('[data-zone-select]');
      if (!this.dialog || !this.form || !this.zoneSelect) return;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      document.addEventListener('click', this.onDocumentClick);

      this.zoneSelect.addEventListener('change', () => this.showLanguagesFor(this.zoneSelect.value));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.querySelectorAll('[data-zone-switcher-close]').forEach((button) =>
        button.addEventListener('click', () => this.close())
      );
      // Clic sur le backdrop (le dialog occupe tout l'espace autour du panneau)
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      // Échap : on passe par close() pour mémoriser le choix et animer
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      // Filet de sécurité : Chrome peut fermer le dialog sans passer par close() (Échap répété)
      this.dialog.addEventListener('close', () => {
        this.dialog.classList.remove('is-visible');
        document.documentElement.classList.remove('zone-switcher-open');
      });

      this.updateTriggers();

      if (this.hasAttribute('data-design-mode')) {
        document.addEventListener('shopify:section:select', (event) => {
          if (this.contains(event.target) || event.target.contains(this)) this.open();
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (this.contains(event.target) || event.target.contains(this)) this.close({ remember: false });
        });
        return;
      }

      if (this.dataset.autoOpen === 'true' && !storage.get()) {
        this.preselectFromGeolocation().finally(() => this.open());
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    onDocumentClick(event) {
      const trigger = event.target.closest('[data-zone-switcher-open]');
      if (!trigger) return;
      event.preventDefault();
      this.open(trigger);
    }

    updateTriggers() {
      const currentOption = this.zoneSelect.querySelector(`option[value="${this.dataset.currentZone}"]`);
      const code = currentOption?.dataset.code;
      document.querySelectorAll('[data-zone-switcher-open]').forEach((trigger) => {
        const codeElement = trigger.querySelector('[data-zone-switcher-code]');
        if (code && codeElement) codeElement.textContent = code;
        trigger.hidden = false;
      });
      document.documentElement.classList.add('zone-switcher-ready');
    }

    showLanguagesFor(zoneId) {
      this.querySelectorAll('[data-zone-languages]').forEach((field) => {
        const isActive = field.dataset.zoneLanguages === zoneId;
        field.hidden = !isActive;
        field.querySelectorAll('select').forEach((select) => (select.disabled = !isActive));
      });
    }

    selectZone(zoneId) {
      this.zoneSelect.value = zoneId;
      this.showLanguagesFor(zoneId);
    }

    /** Présélectionne la zone d'après le pays détecté par Shopify (sans effet si l'appel échoue). */
    preselectFromGeolocation() {
      return fetch('/browsing_context_suggestions.json?country[enabled]=true')
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
          const country = data?.detected_values?.country?.handle?.toUpperCase();
          if (!country) return;
          const option = Array.from(this.zoneSelect.options).find((opt) =>
            (opt.dataset.countries || '').split(',').includes(country)
          );
          if (option) this.selectZone(option.value);
        })
        .catch(() => {});
    }

    open(trigger) {
      if (this.dialog.open) return;
      this.lastTrigger = trigger || null;
      // Réaligne le formulaire sur la zone/langue courantes à chaque ouverture manuelle
      if (trigger) {
        this.selectZone(this.dataset.currentZone);
        const localeSelect = this.querySelector('select[name="locale_code"]');
        if (localeSelect) localeSelect.value = this.dataset.currentLocale;
      }
      document.documentElement.classList.add('zone-switcher-open');
      this.dialog.showModal();
      requestAnimationFrame(() => this.dialog.classList.add('is-visible'));
    }

    close({ remember = true } = {}) {
      if (!this.dialog.open) return;
      if (remember) storage.set('dismissed');
      this.dialog.classList.remove('is-visible');
      const finish = () => {
        this.dialog.close();
        document.documentElement.classList.remove('zone-switcher-open');
        this.lastTrigger?.focus?.();
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
      } else {
        setTimeout(finish, 300);
      }
    }

    onSubmit(event) {
      const zoneOption = this.zoneSelect.selectedOptions[0];
      if (!zoneOption) return;
      storage.set('selected');

      const languageSelect = this.querySelector(`[data-zone-languages="${zoneOption.value}"] select`);

      if (zoneOption.hasAttribute('data-current-store')) {
        // Même boutique : on ne soumet le formulaire localization que si la langue change
        if (languageSelect && languageSelect.value !== this.dataset.currentLocale) return;
        event.preventDefault();
        this.close();
        return;
      }

      event.preventDefault();
      const url = (languageSelect && languageSelect.value) || zoneOption.dataset.url;
      if (url) window.location.href = url;
    }
  }

  customElements.define('zone-switcher', ZoneSwitcher);
}
